import { useRef } from 'react';
import { Download, Linkedin, Mail } from 'lucide-react';
import { Button } from './ui/button';

// Specialty translations
const SPECIALTY_LABELS = {
  therapeute: 'Thérapeute',
  naturopathe: 'Naturopathe',
  sophrologue: 'Sophrologue',
  psychologue: 'Psychologue',
  osteopathe: 'Ostéopathe',
  hypnotherapeute: 'Hypnothérapeute',
  coach: 'Coach de vie',
  orthophoniste: 'Orthophoniste',
  nutritionniste: 'Nutritionniste'
};

/**
 * Generate SVG badge for social media / LinkedIn / email signature
 */
const generateBadgeSVG = (practitioner, badgeType = 'verified') => {
  const firstName = practitioner?.user?.first_name || practitioner?.first_name || '';
  const lastName = practitioner?.user?.last_name || practitioner?.last_name || '';
  const specialty = practitioner?.specialty || '';
  const specialtyLabel = SPECIALTY_LABELS[specialty] || specialty;
  
  // Badge colors based on type
  const colors = {
    verified: { bg: '#4A7C59', accent: '#3d6b4a' },
    solidaire: { bg: '#be185d', accent: '#9d174d' },
    bronze: { bg: '#b45309', accent: '#92400e' },
    argent: { bg: '#64748b', accent: '#475569' },
    or: { bg: '#ca8a04', accent: '#a16207' },
    emeraude: { bg: '#059669', accent: '#047857' },
    rubis: { bg: '#dc2626', accent: '#b91c1c' }
  };
  
  const color = colors[badgeType] || colors.verified;
  
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 120" width="400" height="120">
  <defs>
    <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:${color.bg};stop-opacity:1" />
      <stop offset="100%" style="stop-color:${color.accent};stop-opacity:1" />
    </linearGradient>
  </defs>
  
  <!-- Background -->
  <rect width="400" height="120" rx="16" fill="url(#bgGrad)"/>
  
  <!-- Logo Circle with Check -->
  <circle cx="60" cy="60" r="35" fill="rgba(255,255,255,0.15)"/>
  <circle cx="60" cy="60" r="28" fill="white"/>
  
  <!-- Leaf/Heart icon with check -->
  <path d="M60 35 C45 35, 35 50, 35 60 C35 75, 50 85, 60 85 C70 85, 85 75, 85 60 C85 50, 75 35, 60 35" fill="${color.bg}" opacity="0.2"/>
  <path d="M52 60 L58 66 L70 52" stroke="${color.bg}" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  
  <!-- Text Content -->
  <text x="115" y="42" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="white">
    Opora Vie ${badgeType === 'verified' ? 'Praticien Vérifié' : badgeType.charAt(0).toUpperCase() + badgeType.slice(1)}
  </text>
  
  <text x="115" y="68" font-family="Arial, sans-serif" font-size="20" font-weight="bold" fill="white">
    Pr. ${firstName} ${lastName}
  </text>
  
  <text x="115" y="92" font-family="Arial, sans-serif" font-size="12" fill="rgba(255,255,255,0.8)">
    ${specialtyLabel} · apora.net
  </text>
</svg>`.trim();
  
  return svg;
};

/**
 * Download SVG as file
 */
const downloadSVG = (svgContent, filename) => {
  const blob = new Blob([svgContent], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download SVG as PNG
 */
const downloadPNG = (svgContent, filename, width = 800, height = 240) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  const img = new Image();
  const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  
  img.onload = () => {
    ctx.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    
    canvas.toBlob((blob) => {
      const pngUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pngUrl);
    }, 'image/png');
  };
  
  img.src = url;
};

/**
 * Social Badge Preview and Download Component
 */
const SocialBadge = ({ practitioner, badgeType = 'verified' }) => {
  const svgContent = generateBadgeSVG(practitioner, badgeType);
  const firstName = practitioner?.user?.first_name || practitioner?.first_name || '';
  const lastName = practitioner?.user?.last_name || practitioner?.last_name || '';
  const filename = `opora-vie-badge-${firstName.toLowerCase()}-${lastName.toLowerCase()}`;
  
  return (
    <div className="space-y-4">
      {/* Preview */}
      <div 
        className="rounded-xl overflow-hidden shadow-lg"
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
      
      {/* Download buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => downloadSVG(svgContent, `${filename}.svg`)}
        >
          <Download className="w-4 h-4 mr-2" />
          SVG
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => downloadPNG(svgContent, `${filename}.png`)}
        >
          <Download className="w-4 h-4 mr-2" />
          PNG
        </Button>
      </div>
    </div>
  );
};

/**
 * Social Badge Download Card - for Kit de bienvenue
 */
const SocialBadgeCard = ({ practitioner, badgeType = 'verified', title, description }) => {
  const svgContent = generateBadgeSVG(practitioner, badgeType);
  const firstName = practitioner?.user?.first_name || practitioner?.first_name || '';
  const lastName = practitioner?.user?.last_name || practitioner?.last_name || '';
  const filename = `opora-vie-badge-${firstName.toLowerCase()}-${lastName.toLowerCase()}`;
  
  return (
    <div className="border border-sand-200 rounded-xl p-4 bg-white hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Mini preview */}
        <div 
          className="w-24 h-auto rounded-lg overflow-hidden shadow-sm shrink-0"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
        
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm mb-1">{title || 'Badge LinkedIn'}</h4>
          <p className="text-xs text-muted-foreground mb-3">
            {description || 'À ajouter à votre profil LinkedIn ou signature email.'}
          </p>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadSVG(svgContent, `${filename}.svg`)}
              className="text-xs h-7"
            >
              <Download className="w-3 h-3 mr-1" />
              SVG
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadPNG(svgContent, `${filename}.png`)}
              className="text-xs h-7"
            >
              <Download className="w-3 h-3 mr-1" />
              PNG
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { SocialBadge, SocialBadgeCard, generateBadgeSVG, downloadSVG, downloadPNG };
